import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '../../../core/components/ui';

/**
 * FAQ Delete Modal Component
 *
 * Confirmation dialog for deleting FAQ entries.
 * Shows FAQ question preview before deletion.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Object|null} props.faq - FAQ object to delete
 * @param {Function} props.onConfirm - Confirm delete handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.isDeleting - Whether deletion is in progress
 *
 * @example
 * <FAQDeleteModal
 *   isOpen={deleteModalOpen}
 *   faq={faqToDelete}
 *   onConfirm={handleDeleteConfirm}
 *   onCancel={() => setDeleteModalOpen(false)}
 *   isDeleting={isDeleting}
 * />
 */
export function FAQDeleteModal({ isOpen, faq, onConfirm, onCancel, isDeleting }) {
  const { t } = useTranslation();

  const handleConfirm = () => {
    if (faq) {
      onConfirm(faq.id);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={t('faq.deleteTitle', 'Delete FAQ')}
      size="lg"
    >
      <div className="space-y-4">
        <p className="text-subtle">
          {t('faq.deleteConfirm', 'Are you sure you want to delete this FAQ entry? This action cannot be undone.')}
        </p>

        {faq && (
          <div className="bg-surface-alt rounded-lg p-4 border border-border-default">
            <p className="text-xs font-medium text-muted uppercase mb-2">
              {t('faq.question', 'Question')}
            </p>
            <p className="font-medium text-sm text-default break-words">
              {faq.question}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isDeleting}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? t('common.deleting', 'Deleting...') : t('faq.delete', 'Delete')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

FAQDeleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  faq: PropTypes.shape({
    id: PropTypes.string.isRequired,
    question: PropTypes.string.isRequired,
  }),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
};
